import { useState, useEffect, useRef } from 'react'
import { useTranslation } from '../../i18n/LanguageContext'
import { useWordSearch } from './useWordSearch'
import type { Difficulty, SearchMode } from './useWordSearch'
import { activities, themes } from '../../api/client'
import type { ThemeSummary } from '../../api/client'
import type { GameItem } from '../../i18n'
import { toGameItems, toGameItemsFromTheme } from '../utils'
import GameSetupScreen from '../GameSetupScreen'
import SaveErrorBanner from '../SaveErrorBanner'
import { ss } from '../gameSetupStyles'
import { useSessionMode } from '../../professional/SessionModeContext'
import { useGameCompletion } from '../../hooks/useGameCompletion'
import { useGameSession } from '../../hooks/useGameSession'
import { useActivitySession } from '../../hooks/useActivitySession'
import GameCompleteScreen from '../GameCompleteScreen'
import { formatGameTime } from '../utils'
import { PlayScreen } from './WordSearchPlayScreen'
import { s } from './wordSearchStyles'

interface WordSearchConfig {
  mode:       SearchMode
  difficulty: Difficulty
  themeSlug:  string | null
  themeId:    number | null
}

interface WordSearchGameProps {
  onBack: () => void
  onGoToSubscription: () => void
}

type GameScreen = 'setup' | 'playing' | 'complete'

const MODES: SearchMode[] = ['simple', 'diagonals', 'reversed']
const DIFFICULTIES: Difficulty[] = ['easy', 'medium', 'hard']

const MODE_ICONS: Record<SearchMode, string> = {
  simple:    '↔️',
  diagonals: '✳️',
  reversed:  '🔀',
}


export default function WordSearchGame({ onBack, onGoToSubscription }: WordSearchGameProps) {
  const { t, language } = useTranslation()
  const sessionMode = useSessionMode()
  const { savedSession, saveConfig, clear } = useActivitySession<WordSearchConfig>('wordsearch', !sessionMode)
  const restored = savedSession?.config ?? null

  const [screen, setScreen]       = useState<GameScreen>(sessionMode ? 'playing' : (restored ? 'playing' : 'setup'))
  const [mode, setMode]           = useState<SearchMode>((sessionMode?.config.mode as SearchMode) ?? restored?.mode ?? 'simple')
  const [difficulty, setDifficulty] = useState<Difficulty>((sessionMode?.config.difficulty as Difficulty) ?? restored?.difficulty ?? 'easy')

  const [classicItems, setClassicItems]     = useState<GameItem[]>(t.items)
  const [gameItems, setGameItems]           = useState<GameItem[]>(t.items)
  const [availableThemes, setAvailableThemes] = useState<ThemeSummary[]>([])
  const [selectedTheme, setSelectedTheme]   = useState<{ slug: string; id: number } | null>(
    (sessionMode?.config.theme as { slug: string; id: number } | null | undefined) ??
    (restored?.themeSlug != null ? { slug: restored.themeSlug, id: restored.themeId! } : null)
  )
  const [themeLoading, setThemeLoading]     = useState(() => !!(sessionMode?.config.theme) || !!(restored?.themeSlug))

  // Fetch classic items + available themes on mount / language change
  useEffect(() => {
    activities.get('wordsearch')
      .then((detail) => {
        const apiItems = detail.data?.items
        if (apiItems && apiItems.length > 0) setClassicItems(toGameItems(apiItems, language))
      })
      .catch(() => { /* keep i18n fallback */ })

    themes.list('wordsearch')
      .then(setAvailableThemes)
      .catch(() => {})
  }, [language])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (!selectedTheme) setGameItems(classicItems)
  }, [classicItems, selectedTheme])

  useEffect(() => {
    if (!selectedTheme) return
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setThemeLoading(true)
    themes.get(selectedTheme.slug)
      .then((theme) => setGameItems(toGameItemsFromTheme(theme.images, language)))
      .catch(() => setSelectedTheme(null))
      .finally(() => setThemeLoading(false))
  }, [selectedTheme, language])

  const { state, resetGame, tapCell, beginSelection, extendDragSelection, commitSelection } = useWordSearch(difficulty, mode, gameItems)
  const { gameComplete, moves, startTime, endTime, foundWords, grid } = state

  // In session mode: reinitialize game state once the configured theme finishes loading.
  // The hook initializes with classic items on mount (theme not yet fetched); this corrects it.
  const sessionThemeResetDone = useRef(false)
  useEffect(() => {
    if (!sessionMode?.config.theme && !restored?.themeSlug) return
    if (sessionThemeResetDone.current) return
    if (themeLoading) return
    sessionThemeResetDone.current = true
    resetGame()
  }, [themeLoading, sessionMode, resetGame]) // eslint-disable-line react-hooks/exhaustive-deps

  const { sessionId } = useGameSession({
    activitySlug: 'wordsearch',
    difficulty,
    mode,
    enabled: screen === 'playing',
  })

  const { saveError, clearSaveError } = useGameCompletion({
    gameComplete,
    activitySlug:  'wordsearch',
    difficulty,
    mode,
    moves,
    startTime,
    endTime,
    pairsTotal:   grid.placedWords.length,
    pairsMatched: foundWords.length,
    themeId:      selectedTheme?.id,
    sessionId,
    onCompleted:  () => { clear(); setScreen('complete') },
  })

  const handleStart = () => {
    resetGame()
    saveConfig({ mode, difficulty, themeSlug: selectedTheme?.slug ?? null, themeId: selectedTheme?.id ?? null })
    setScreen('playing')
  }
  const handlePlayAgain = () => {
    resetGame()
    saveConfig({ mode, difficulty, themeSlug: selectedTheme?.slug ?? null, themeId: selectedTheme?.id ?? null })
    setScreen('playing')
  }
  const handleChangeLevel = () => { clear(); setScreen('setup') }

  if ((sessionMode || restored?.themeSlug) && themeLoading) {
    return <div style={s.loadRoot}>…</div>
  }

  return (
    <div style={s.root}>
      {saveError && <SaveErrorBanner message={t.common.saveError} dismissLabel={t.common.dismissError} onDismiss={clearSaveError} />}
      {screen === 'setup' && (
        <SetupScreen
          mode={mode}
          difficulty={difficulty}
          availableThemes={availableThemes}
          selectedTheme={selectedTheme}
          themeLoading={themeLoading}
          onModeChange={setMode}
          onDifficultyChange={setDifficulty}
          onThemeChange={(th) => setSelectedTheme(th ? { slug: th.slug, id: th.id } : null)}
          onStart={handleStart}
          onBack={onBack}
          onGoToSubscription={onGoToSubscription}
        />
      )}
      {screen === 'playing' && (
        <PlayScreen
          state={state}
          difficulty={difficulty}
          mode={mode}
          onTap={tapCell}
          onBeginDrag={beginSelection}
          onExtendDrag={extendDragSelection}
          onCommitDrag={commitSelection}
          onBack={sessionMode ? onBack : () => { clear(); setScreen('setup') }}
        />
      )}
      {screen === 'complete' && (
        <GameCompleteScreen
          emoji="🔍"
          title={t.wordsearch.complete.title}
          subtitle={t.wordsearch.complete.subtitle}
          stats={[
            { value: `${foundWords.length}/${grid.placedWords.length}`, label: t.wordsearch.complete.wordsLabel },
            { value: formatGameTime(startTime, endTime), label: t.wordsearch.complete.timeLabel },
            { value: t.wordsearch.difficulty[difficulty].label, label: t.wordsearch.complete.levelLabel },
          ]}
          playAgainLabel={t.wordsearch.complete.playAgain}
          changeLevelLabel={t.wordsearch.complete.changeLevel}
          backLabel={t.wordsearch.complete.backToDashboard}
          onPlayAgain={handlePlayAgain}
          onChangeLevel={handleChangeLevel}
          onBack={onBack}
        />
      )}
    </div>
  )
}

/* ── Setup Screen ────────────────────────────────────────────── */
function SetupScreen({
  mode, difficulty, availableThemes, selectedTheme, themeLoading,
  onModeChange, onDifficultyChange, onThemeChange, onStart, onBack, onGoToSubscription,
}: {
  mode: SearchMode; difficulty: Difficulty; availableThemes: ThemeSummary[]
  selectedTheme: { slug: string; id: number } | null; themeLoading: boolean
  onModeChange: (m: SearchMode) => void; onDifficultyChange: (d: Difficulty) => void
  onThemeChange: (t: ThemeSummary | null) => void
  onStart: () => void; onBack: () => void; onGoToSubscription: () => void
}) {
  const { t, language } = useTranslation()
  const ws = t.wordsearch

  const selectedThemeData = selectedTheme
    ? availableThemes.find(th => th.id === selectedTheme.id)
    : undefined

  function themeWordCount(diff: Difficulty): number {
    if (!selectedThemeData?.wordsearchFit) return Infinity
    const lang = language === 'en' ? 'en' : 'es'
    return selectedThemeData.wordsearchFit[lang][diff] ?? 0
  }

  const isIncompatible = selectedTheme !== null && themeWordCount(difficulty) < 4

  const difficulties = DIFFICULTIES.map((id) => {
    const cfg = ws.difficulty[id]
    const incompatible = selectedTheme !== null && themeWordCount(id) < 4
    return {
      id,
      label: cfg.label,
      hint: incompatible
        ? `${themeWordCount(id)} palabra${themeWordCount(id) === 1 ? '' : 's'}`
        : cfg.hint,
    }
  })

  return (
    <GameSetupScreen
      title={ws.title}
      subtitle={ws.subtitle}
      backLabel={ws.backToDashboard}
      onBack={onBack}
      availableThemes={availableThemes}
      selectedTheme={selectedTheme}
      onThemeChange={onThemeChange}
      classicEmoji="🔤"
      classicLabel={ws.classicTheme}
      chooseThemeLabel={ws.chooseThemeTitle}
      onGoToSubscription={onGoToSubscription}
      difficulties={difficulties}
      selectedDifficulty={difficulty}
      onDifficultyChange={(id) => onDifficultyChange(id as Difficulty)}
      chooseDifficultyLabel={ws.chooseDifficultyTitle}
      startLabel={ws.startBtn}
      onStart={onStart}
      themeLoading={themeLoading}
      startDisabled={isIncompatible}
      preview={
        <>
          <GridPreview />
          {isIncompatible && (
            <div style={{
              background: '#FFF3E0', border: '1px solid #FFB74D', borderRadius: 10,
              padding: '10px 14px', fontSize: '0.9rem', color: '#E65100', textAlign: 'center',
              marginTop: 12,
            }}>
              {language === 'es'
                ? `Este tema solo tiene ${themeWordCount(difficulty)} palabra${themeWordCount(difficulty) === 1 ? '' : 's'} válidas para esta dificultad. Elige otra dificultad o cambia de tema.`
                : `This theme only has ${themeWordCount(difficulty)} valid word${themeWordCount(difficulty) === 1 ? '' : 's'} for this difficulty. Choose a different difficulty or theme.`
              }
            </div>
          )}
        </>
      }
    >
      {/* Mode selector */}
      <section className="fade-up" style={{ animationDelay: '0.1s' }}>
        <h2 style={ss.optionLabel}>{ws.chooseModeTitle}</h2>
        <div style={ss.modeGrid}>
          {MODES.map((id) => {
            const cfg = ws.modes[id]
            return (
              <button
                key={id}
                onClick={() => onModeChange(id)}
                style={{ ...ss.modeCard, ...(mode === id ? ss.modeCardActive : {}) }}
                aria-pressed={mode === id}
              >
                <span style={{ fontSize: '1.5rem', lineHeight: 1 }}>{MODE_ICONS[id]}</span>
                <span style={{ fontSize: '1rem', fontWeight: 700, color: '#2E1F14' }}>{cfg.label}</span>
                <span style={{ fontSize: '0.875rem', color: '#6B4C3B', fontWeight: 500, lineHeight: 1.4 }}>{cfg.description}</span>
              </button>
            )
          })}
        </div>
      </section>
    </GameSetupScreen>
  )
}

/* ── Grid Preview (static illustration) ─────────────────────── */
const PREVIEW_GRID = [
  ['S','O','P','A','L'],
  ['B','R','E','M','F'],
  ['G','H','T','J','K'],
  ['M','N','R','A','Q'],
  ['V','X','A','L','B'],
]
const PREVIEW_FOUND = new Set(['0-0','0-1','0-2','0-3'])

function GridPreview() {
  return (
    <div style={s.previewWrap}>
      <div style={s.previewGrid}>
        {PREVIEW_GRID.map((row, r) =>
          row.map((letter, c) => {
            const key = `${r}-${c}`
            const isFound = PREVIEW_FOUND.has(key)
            return (
              <div
                key={key}
                style={{
                  ...s.previewCell,
                  background:  isFound ? '#52B788' : '#F5F0E8',
                  color:       isFound ? '#FFFFFF'  : '#3D2B1F',
                  fontWeight:  isFound ? 800        : 600,
                  borderRadius: 6,
                }}
              >
                {letter}
              </div>
            )
          })
        )}
      </div>
      <div style={s.previewWordRow}>
        <span style={{ ...s.previewWord, textDecoration: 'line-through', color: '#52B788' }}>SOPA</span>
        <span style={s.previewWord}>LETRA</span>
        <span style={s.previewWord}>TRAMA</span>
      </div>
    </div>
  )
}


